/**
 * FTR6: target selection over manifest bodies, with distance + closing speed
 * computed in Float64 world space.
 */
import {
  sub,
  length,
  dot,
  scale,
  normalize,
  signalMarkerPos,
  type Vec3,
  type NpcShip,
  type SignalSource,
} from '@blackmarble/sim-core';
import type { World } from '../sim/world.js';

export interface TargetInfo {
  name: string;
  worldPos: Vec3;
  distanceKm: number;
  closingSpeed: number; // +ve = approaching
  isSignal?: boolean; // M3: true when the locked nav-target is a signal source
}

/** Closing speed of a velocity toward a world point (+ve = approaching). Pure. */
export const closingSpeedTo = (shipPos: Vec3, shipVel: Vec3, worldPos: Vec3): number => {
  const to = sub(worldPos, shipPos);
  const d = length(to);
  return d > 0 ? dot(shipVel, scale(to, 1 / d)) : 0;
};

/** P4 combat: a locked NPC target (nearest within the aim cone). */
export interface CombatLock {
  id: string;
  pos: Vec3;
  distanceKm: number;
}

/**
 * Nearest NPC inside the aim cone in front of the ship. coneMin ~0.985 = ~10°
 * half-angle. Returns null if nothing is in the cone. Pure (no Three.js).
 */
export const acquireCombatTarget = (
  npcs: readonly NpcShip[],
  shipPos: Vec3,
  forward: Vec3,
  coneMin = 0.985,
): CombatLock | null => {
  const fwd = normalize(forward);
  let best: CombatLock | null = null;
  for (const n of npcs) {
    const to = sub(n.pos, shipPos);
    const d = length(to);
    if (d <= 0) continue;
    if (dot(fwd, scale(to, 1 / d)) < coneMin) continue;
    if (!best || d < best.distanceKm) best = { id: n.id, pos: n.pos, distanceKm: d };
  }
  return best;
};

/** A live custom nav-target (station / contact) resolved each frame by `resolve`. */
interface CustomTarget {
  name: string;
  resolve: () => Vec3 | null;
}

export class TargetManager {
  private index = 0;
  // M3: optional signal nav-lock. When set it OVERRIDES the body target for the
  // HUD readout + bracket, giving heading guidance to the signal.
  private signal: SignalSource | null = null;
  // M4: optional custom nav-lock (station/contact) — a live position resolver so a
  // moving station/ship stays correctly bracketed. Highest priority when set.
  private custom: CustomTarget | null = null;

  constructor(private readonly world: World) {}

  get currentName(): string {
    if (this.custom) return this.custom.name;
    if (this.signal) return this.signal.title;
    return this.world.bodies[this.index]?.entry.name ?? '';
  }

  /** Select a body by name (e.g. default target on spawn). No-op if absent. */
  select(name: string): void {
    const i = this.world.bodies.findIndex((b) => b.entry.name === name);
    if (i >= 0) {
      this.index = i;
      this.signal = null; // a body selection clears any signal/custom lock
      this.custom = null;
    }
  }

  /** M3: lock the given signal as the nav-target (N key), or clear with null. */
  lockSignal(sig: SignalSource | null): void {
    this.signal = sig;
    if (sig) this.custom = null;
  }

  /** M4: lock a live custom target (station/contact) by name + position resolver. */
  lockCustom(name: string, resolve: () => Vec3 | null): void {
    this.custom = { name, resolve };
    this.signal = null;
  }

  next(): void {
    this.signal = null; // cycling bodies clears the signal/custom lock
    this.custom = null;
    this.index = (this.index + 1) % this.world.bodies.length;
  }
  prev(): void {
    this.signal = null;
    this.custom = null;
    const n = this.world.bodies.length;
    this.index = (this.index - 1 + n) % n;
  }

  info(shipPos: Vec3, shipVel: Vec3): TargetInfo | null {
    if (this.custom) {
      const wp = this.custom.resolve();
      if (wp) {
        return {
          name: this.custom.name,
          worldPos: wp,
          distanceKm: length(sub(wp, shipPos)),
          closingSpeed: closingSpeedTo(shipPos, shipVel, wp),
          isSignal: false,
        };
      }
      this.custom = null; // resolver gone (e.g. ship despawned) — fall through
    }
    if (this.signal) {
      const wp = signalMarkerPos(this.signal);
      return {
        name: this.signal.title,
        worldPos: wp,
        distanceKm: length(sub(wp, shipPos)),
        closingSpeed: closingSpeedTo(shipPos, shipVel, wp),
        isSignal: true,
      };
    }
    const wb = this.world.bodies[this.index];
    if (!wb) return null;
    return {
      name: wb.entry.name,
      worldPos: wb.worldPos,
      distanceKm: length(sub(wb.worldPos, shipPos)),
      closingSpeed: closingSpeedTo(shipPos, shipVel, wb.worldPos),
      isSignal: false,
    };
  }
}
